/**
 * What has been issued to one volunteer — ADMIN and TEAM (refinement 2).
 *
 * ---------------------------------------------------------------------------
 * WHY THIS TAB EXISTS SEPARATELY FROM THE OTHER TWO
 * ---------------------------------------------------------------------------
 * The other tabs answer "who should get a certificate" and are an admin's
 * worklist. This one answers a question a field coordinator is asked at the
 * door: *has this person had a certificate, and what did it say?* It is a read,
 * it names one volunteer rather than ranking all of them, and it is the only
 * certificate screen a TEAM account can open.
 *
 * Reading does not become issuing. There is no button here that writes
 * anything, and the API agrees: `GET /certificates` is ADMIN or TEAM while
 * every POST stays ADMIN, so a coordinator who found this screen by guessing a
 * URL still could not send a document in Parinaam's name.
 *
 * ---------------------------------------------------------------------------
 * A DROPDOWN, NOT A TABLE OF VOLUNTEERS
 * ---------------------------------------------------------------------------
 * The first version of this screen made you work a paginated directory with a
 * search box and a status filter before you could ask the actual question. That
 * is a lot of furniture for "pick a person": the volunteer is already known to
 * whoever opened the tab, so the fastest path is to type the name and choose.
 *
 * The search runs on the API rather than over a list held here, because the
 * directory is longer than one page (`MAX_PAGE_SIZE` is 100) — a dropdown that
 * filtered only what the page happened to hold would quietly fail to find
 * anybody past the first hundred, which is the worst kind of wrong.
 */

import { useCallback, useState } from 'react';
import { formatDuration, personName } from '@parinaam-vms/shared';
import type { CertificateListItem, VolunteerListItem } from '@parinaam-vms/shared';
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  LoadingState,
  Pager,
  SearchSelectInput,
} from '../../components/common';
import type { Column } from '../../components/common';
import { useVolunteers } from '../../hooks/useVolunteers';
import { useVolunteerCertificates } from '../../hooks/useCertificates';
import { useDebounced } from '../../hooks/useDebounced';
import { fetchCertificatePdf } from '../../services/api/certificates';
import { formatDate } from '../../utils/format';
import { CertificateViewer } from './CertificateViewer';
import styles from './CertificatesPage.module.css';

type ChosenVolunteer = { volunteer_id: string; name: string; volunteer_code: string };

/**
 * How many names the dropdown offers.
 *
 * Ten fit in the panel without scrolling. More than that is not a longer list
 * to read, it is a sign the search term needs another word — and a scrolling
 * dropdown of fifty near-identical names is slower than typing a surname.
 */
const PICKER_RESULTS = 10;

export function VolunteerCertificatesTab() {
  const [term, setTerm] = useState('');
  const [chosen, setChosen] = useState<ChosenVolunteer | null>(null);
  const [page, setPage] = useState(1);
  const [viewing, setViewing] = useState<CertificateListItem | null>(null);

  // One request per pause, not one per keystroke.
  const search = useDebounced(term);

  const volunteers = useVolunteers({
    search: search.trim() || undefined,
    page: 1,
    page_size: PICKER_RESULTS,
  });
  const certificates = useVolunteerCertificates(chosen?.volunteer_id, page);

  /*
   * Memoised on the id alone. `CertificateViewer` refetches whenever `source`
   * changes identity, so an inline arrow would rebuild the PDF on every render
   * — including every keystroke in the picker above it.
   */
  const viewingId = viewing?.certificate_id;
  const loadPdf = useCallback(() => {
    if (!viewingId) return Promise.reject(new Error('No certificate selected.'));
    return fetchCertificatePdf(viewingId);
  }, [viewingId]);

  const options = (volunteers.data?.items ?? []).map((volunteer: VolunteerListItem) => ({
    value: volunteer.volunteer_id,
    label: personName(volunteer),
    // The code and the email, because two volunteers share a name more often
    // than anybody expects and the name alone is not enough to choose between
    // them.
    hint: [volunteer.volunteer_code, volunteer.email].filter(Boolean).join(' · '),
  }));

  const columns: Column<CertificateListItem>[] = [
    { key: 'code', header: 'Reference', render: (row) => row.certificate_code },
    {
      key: 'type',
      header: 'Type',
      render: (row) => (row.certificate_type === 'CUSTOM' ? 'Custom' : 'Volunteer hours'),
    },
    {
      key: 'recognised',
      header: 'Recognised',
      // A custom certificate has no hours by design, and an em dash says that
      // more honestly than "0h" would.
      render: (row) =>
        row.total_minutes === null
          ? '—'
          : `${formatDuration(row.total_minutes)} · ${row.activity_count} activities`,
    },
    { key: 'issued', header: 'Issued', render: (row) => formatDate(row.issued_at) },
    { key: 'sent_to', header: 'Sent to', render: (row) => row.recipient_email ?? 'Not emailed' },
    {
      key: 'actions',
      header: '',
      render: (row) => (
        <Button
          variant="secondary"
          onClick={() => setViewing(viewing?.certificate_id === row.certificate_id ? null : row)}
        >
          {viewing?.certificate_id === row.certificate_id ? 'Hide' : 'Preview'}
        </Button>
      ),
    },
  ];

  const rows = certificates.data?.items ?? [];

  return (
    <>
      <Card>
        <div className={styles.filters}>
          <SearchSelectInput
            label="Volunteer"
            placeholder="Select a volunteer"
            searchPlaceholder="Search by name, code or email"
            options={options}
            loading={volunteers.isFetching}
            emptyMessage={
              search.trim() ? 'No volunteer matches that.' : 'Type a name to search the directory.'
            }
            value={chosen?.volunteer_id ?? null}
            valueLabel={chosen ? `${chosen.name} · ${chosen.volunteer_code}` : undefined}
            onSearch={setTerm}
            onChange={(value, option) => {
              if (value === null || option === null) {
                setChosen(null);
              } else {
                // The code is parsed back out of the hint rather than carried
                // separately: the picker's contract is {value, label, hint},
                // and widening it for one caller would spread into the others.
                setChosen({
                  volunteer_id: value,
                  name: option.label,
                  volunteer_code: option.hint?.split(' · ')[0] ?? '',
                });
              }
              setPage(1);
              setViewing(null);
            }}
          />
        </div>

        {!chosen ? (
          <EmptyState
            title="Choose a volunteer"
            message="Pick somebody above to see the certificates issued to them."
          />
        ) : certificates.isPending ? (
          <LoadingState label="Loading certificates…" />
        ) : certificates.isError ? (
          <ErrorState error={certificates.error} onRetry={() => void certificates.refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No certificates yet"
            message={`${chosen.name} has not been issued a certificate. An admin can issue one from the other tabs.`}
          />
        ) : (
          <>
            <DataTable<CertificateListItem>
              caption={`Certificates issued to ${chosen.name}`}
              rows={rows}
              columns={columns}
              getRowKey={(row) => row.certificate_id}
            />
            <Pager pagination={certificates.data?.pagination} onPageChange={setPage} />
          </>
        )}
      </Card>

      {viewing ? (
        <CertificateViewer
          title={viewing.certificate_code}
          subtitle={`Issued to ${viewing.volunteer_name} on ${formatDate(viewing.issued_at)}`}
          source={loadPdf}
          downloadName={`Parinaam-Certificate-${viewing.certificate_code}.pdf`}
          onClose={() => setViewing(null)}
        />
      ) : null}
    </>
  );
}
